import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './OverflowMenu.module.css';

/**
 * Compact "More actions" dropdown.
 * Pass `items` as an array of { label, icon?, onClick, disabled?, danger?, divider? }
 * `divider: true` items render as a horizontal rule and have no other behavior.
 *
 * The menu is portaled to document.body and positioned with `position: fixed`
 * so it escapes any ancestor with `overflow: hidden`.
 */
export default function OverflowMenu({ items, label = '⋯' }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null); // { top, right } in viewport coords
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const updatePos = () => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    setPos({
      top: r.bottom + 6,
      right: window.innerWidth - r.right,
    });
  };

  const handleToggle = () => {
    if (!open) updatePos();
    setOpen(o => !o);
  };

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onScrollOrResize = () => setOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  return (
    <div className={styles.wrap}>
      <button
        ref={triggerRef}
        className={styles.trigger}
        onClick={handleToggle}
        aria-haspopup="menu"
        aria-expanded={open}
        title="More actions"
      >
        {label}
      </button>
      {open && pos && createPortal(
        <div
          ref={menuRef}
          className={styles.menu}
          role="menu"
          style={{ position: 'fixed', top: pos.top, right: pos.right }}
        >
          {items.map((it, i) => {
            if (it.divider) return <div key={i} className={styles.divider} />;
            return (
              <button
                key={i}
                role="menuitem"
                className={`${styles.item} ${it.danger ? styles.itemDanger : ''}`}
                disabled={it.disabled}
                onClick={() => { setOpen(false); it.onClick?.(); }}
              >
                {it.icon && <span className={styles.icon}>{it.icon}</span>}
                <span>{it.label}</span>
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}
